import React, { useCallback, useMemo, useState, type FC } from 'react';
import Link from 'next/link';
import Carousel from 'react-material-ui-carousel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Image } from 'components/ImageComponent';

import styles from './_SimpleCarousel.module.scss';
import { ArrowLeft } from 'components/icons';
import type { SimpleCarouselProps } from './types';
import { DEFAULT_CAROUSEL_SETTINGS, MAX_IMAGES_IN_CAROUSEL } from './constants';
import CarouselDots from './carousel-dots';
import { shouldLoadImage, calculateNextIndex } from './helpers';

const SimpleCarousel: FC<SimpleCarouselProps> = ({
  images,
  settings = {},
  linkTo,
}) => {
  const mergedSettings: SimpleCarouselProps['settings'] = {
    ...DEFAULT_CAROUSEL_SETTINGS,
    ...settings,
  };
  const slides = useMemo(
    () => images.slice(0, MAX_IMAGES_IN_CAROUSEL),
    [images]
  );

  const { infinite, maxVisibleDots, enableSwipe } = mergedSettings;

  const [currentIndex, setCurrentIndex] = useState(0);

  const isMobile = useMediaQuery(`(max-width:768px)`);

  const goToSlide = useCallback(
    (index: number) => {
      const total = slides.length;
      const direction = index - currentIndex;
      const nextIndex = calculateNextIndex(
        currentIndex,
        direction,
        total,
        infinite
      );

      if (nextIndex !== null) {
        setCurrentIndex(nextIndex);
      }
    },
    [currentIndex, slides.length, infinite]
  );

  const handleCarouselChange = useCallback(
    (slide?: number) => {
      if (slide !== undefined && slide !== currentIndex) {
        setCurrentIndex(slide);
      }
    },
    [currentIndex]
  );

  const renderSlide = (image: { url: string }, index: number) => {
    const shouldLoad = shouldLoadImage(
      index,
      currentIndex,
      slides.length,
      infinite
    );
    if (!shouldLoad) {
      return null;
    }
    const slideContent = (
      <Image
        key={`slide-${image.url}-${index}`}
        src={image.url}
        alt={`carousel slide ${index + 1}`}
        style={{
          aspectRatio: '16/9',
          maxWidth: '100%',
          maxHeight: '270px',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    );
    return linkTo ? (
      <Link key={`slide-${image.url}-${index}`} href={linkTo} passHref>
        <a href={linkTo} className={styles.carouselSlidesLink}>
          {slideContent}
        </a>
      </Link>
    ) : (
      slideContent
    );
  };

  const carouselContent = (
    <Carousel
      autoPlay={false}
      animation="slide"
      navButtonsAlwaysVisible={!isMobile}
      indicators={true}
      cycleNavigation={infinite}
      height={enableSwipe ? 250 : 270}
      swipe={enableSwipe}
      className={styles.carousel}
      PrevIcon={<ArrowLeft />}
      NextIcon={<ArrowLeft className={styles.next} />}
      activeIndicatorIconButtonProps={{
        className: styles.active,
      }}
      indicatorContainerProps={{ className: styles.hide }}
      navButtonsProps={{
        className: styles.navPosition,
      }}
      navButtonsWrapperProps={{
        className: styles.navButtonWrapper,
      }}
      onChange={handleCarouselChange}
      index={currentIndex}
    >
      {slides.map((img, i) => renderSlide(img, i))}
    </Carousel>
  );

  return (
    <article
      className={styles.carouselContainer}
      style={{
        height: enableSwipe ? 250 : 270,
      }}
    >
      {carouselContent}
      <CarouselDots
        totalItems={slides.length}
        currentIndex={currentIndex}
        maxVisibleDots={maxVisibleDots}
        onDotClick={goToSlide}
      />
    </article>
  );
};

export default SimpleCarousel;
